/*
 * Spend-threshold gift box (Post-Activity liquidation, Q4 2026).
 *
 * Keeps exactly one gift line in the cart while the order qualifies, and none
 * when it does not. The line carries the hidden property `_oleus_gift`; the
 * free-gift Shopify Function (oleus-discounts-app) is what makes it €0, and it
 * only does so when the price paid for everything else is strictly over the
 * threshold. This script follows the Function's verdict:
 *
 *   - no gift line, cart total over the threshold  -> add one
 *   - gift line present and €0                      -> keep it, quantity 1
 *   - gift line present and charged                 -> the Function said no:
 *                                                      take it out
 *
 * The customer may remove the box (remove link marked data-kgf-remove); it is
 * then not re-added for the rest of the browser session.
 *
 * `cart.total_price` is after every discount, including a code applied in the
 * cart through a /discount/ link, and excludes shipping: the same figure the
 * threshold is defined on. A removal remembers the cart total it happened at,
 * so a cart the Function rejects is not re-offered the box until something
 * changes (no add/remove loop when the theme and the Function disagree, e.g.
 * on a code the Function has no rate for).
 *
 * While the 1+1 campaign (kinetic-bogo.js) covers the gift's own variant, the
 * box stands down whenever the cart already holds that variant: the native
 * "Buy X get Y" discount counts the gift box as one of its free boxes, so the
 * Function has nothing left to free and a 1+1 box ends up charged. Those
 * customers get the 1+1 instead.
 *
 * The box carries the visible "Best before" property (bestBeforeKey /
 * bestBefore), like a paid line, so checkout shows its date too.
 *
 * Config: #kinetic-gift-config, rendered in layout/theme.liquid from shop
 * metafield oleus.gift_offer.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('kinetic-gift-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  if (!config || !config.variantId) return;

  var PROPERTY = '_oleus_gift';
  var PROPERTY_VALUE = 'post-activity-70';
  var VETO_KEY = 'oleus-gift:veto';
  // Set when the customer removes the box themselves: no re-adding this session.
  var DECLINED_KEY = 'oleus-gift:declined';
  // Hard stop against any loop: at most this many cart writes per window.
  var MAX_WRITES = 4;
  var WRITE_WINDOW_MS = 15000;

  var cartUrl =
    (window.theme && window.theme.routes && window.theme.routes.cart) ||
    ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart';
  cartUrl = cartUrl.replace(/\/$/, '');

  // Variants in the 1+1 campaign, from kinetic-entry-popup's config.
  var bogoVariants = [];
  try {
    var bogoEl = document.getElementById('kinetic-bogo-config');
    bogoVariants = (bogoEl && JSON.parse(bogoEl.textContent).variants) || [];
  } catch (error) {
    bogoVariants = [];
  }

  var busy = false;
  var queued = false;
  var dispatching = false;
  var writes = [];

  function offerLive() {
    if (config.active !== true) return false;
    if (config.endsAt) {
      var end = Date.parse(config.endsAt);
      if (!isNaN(end) && Date.now() > end) return false;
    }
    return true;
  }

  function thresholdCents() {
    var rate = 1;
    try {
      rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    } catch (error) {
      rate = 1;
    }
    return Math.round((config.thresholdCents || 7000) * rate);
  }

  function readVeto() {
    try {
      var raw = sessionStorage.getItem(VETO_KEY);
      return raw === null ? null : parseInt(raw, 10);
    } catch (error) {
      return null;
    }
  }

  function writeVeto(total) {
    try {
      if (total === null) sessionStorage.removeItem(VETO_KEY);
      else sessionStorage.setItem(VETO_KEY, String(total));
    } catch (error) {
      /* storage blocked: the write cap still prevents a loop */
    }
  }

  function declined() {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function canWrite() {
    var now = Date.now();
    writes = writes.filter(function (t) {
      return now - t < WRITE_WINDOW_MS;
    });
    if (writes.length >= MAX_WRITES) return false;
    writes.push(now);
    return true;
  }

  function isGift(item) {
    return Boolean(item && item.properties && item.properties[PROPERTY]);
  }

  function json(url, body) {
    var options = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function getCart() {
    return json(cartUrl + '.js');
  }

  /* Hand the fresh cart to the theme the same way kinetic-builder does:
     theme:cart:change carries the object, theme:cart:reload re-renders the
     drawer / cart page. Our own listener ignores these. */
  function announce(cart) {
    dispatching = true;
    try {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    } finally {
      dispatching = false;
    }
  }

  function giftProperties() {
    var p = {};
    p[PROPERTY] = PROPERTY_VALUE;
    if (config.bestBefore && config.bestBeforeKey) p[config.bestBeforeKey] = config.bestBefore;
    return p;
  }

  function zeroAll(gifts) {
    var updates = {};
    gifts.forEach(function (item) {
      updates[item.key] = 0;
    });
    return updates;
  }

  function bogoClash(cart) {
    if (bogoVariants.indexOf(config.variantId) === -1) return false;
    return (cart.items || []).some(function (item) {
      return item.variant_id === config.variantId && !isGift(item);
    });
  }

  /* Pure: what the cart needs, or null when it is already right. */
  function decide(cart) {
    var gifts = (cart.items || []).filter(isGift);

    // Offer off, ended, or the 1+1 owns this box: no gift line may stay.
    if (!offerLive() || bogoClash(cart)) {
      return gifts.length ? { action: 'update', updates: zeroAll(gifts) } : null;
    }

    if (!gifts.length) {
      if (!config.available) return null;
      if (declined()) return null;
      if (!(cart.total_price > thresholdCents())) return null;
      if (readVeto() === cart.total_price) return null;
      return { action: 'add' };
    }

    // Exactly one gift line, quantity 1.
    if (gifts.length > 1 || gifts[0].quantity !== 1) {
      var updates = zeroAll(gifts);
      updates[gifts[0].key] = 1;
      return { action: 'update', updates: updates };
    }

    // Charged = the Function did not free it = the order does not qualify.
    if (gifts[0].final_line_price > 0) {
      return { action: 'update', updates: zeroAll(gifts), veto: true };
    }

    return null;
  }

  function execute(decision) {
    var request =
      decision.action === 'add'
        ? json(cartUrl + '/add.js', { items: [{ id: config.variantId, quantity: 1, properties: giftProperties() }] })
        : json(cartUrl + '/update.js', { updates: decision.updates });

    return request.then(getCart).then(function (fresh) {
      if (decision.veto) writeVeto(fresh.total_price);
      return fresh;
    });
  }

  function reconcile(cart) {
    if (busy) {
      queued = true;
      return;
    }
    busy = true;

    (cart ? Promise.resolve(cart) : getCart())
      .then(function (current) {
        if (!current || !current.items) return null;
        var decision = decide(current);
        if (!decision) {
          // A free gift line means the Function agrees: forget any old veto.
          if (current.items.some(isGift)) writeVeto(null);
          return null;
        }
        if (!canWrite()) return null;
        return execute(decision);
      })
      .then(function (fresh) {
        if (fresh) announce(fresh);
        return fresh;
      })
      .catch(function () {
        /* Out of stock (422) or network: leave the cart as it is. The
           Function still guarantees nothing is free that should not be. */
        return null;
      })
      .then(function (fresh) {
        busy = false;
        if (queued) {
          queued = false;
          reconcile(null);
        } else if (fresh) {
          // Check the result once more: an added box must come back €0, or go.
          reconcile(fresh);
        }
      });
  }

  // Capture phase: runs before the theme's remove handler re-renders the row.
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest || !event.target.closest('[data-kgf-remove]')) return;
      try {
        sessionStorage.setItem(DECLINED_KEY, '1');
      } catch (error) {
        /* storage blocked: the box may come back, the write cap stops a loop */
      }
    },
    true
  );

  document.addEventListener('theme:cart:change', function (event) {
    if (dispatching) return;
    reconcile(event && event.detail && event.detail.cart && event.detail.cart.items ? event.detail.cart : null);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      reconcile(null);
    });
  } else {
    reconcile(null);
  }
})();
